/**
 * Tasks and Map realtime bridge.
 *
 * Location protocol (`hm-location-v1`):
 * 1. Connect WSS → hello
 * 2. Auth with task context to receive coordinates, e.g.
 *    `{ type: "auth", role: "technician", token: "<staff_id>", task_type: "order", task_id: 171 }`
 * 3. Server replies `auth_ok` with nested `location` (lat/lng); further pushes may be `type: "location"`
 *
 * Also listens to legacy push names (driver.location / technician.location) and board refresh events.
 */

import { onMounted, onUnmounted, ref, watch, type Ref } from 'vue'
import type { TasksMapPanelTab, TasksMapPerson, TasksMapPoint } from '@/api/tasksMap'
import {
  WsClient,
  type WsAuthPayload,
  type WsConnectionStatus,
  type WsIncomingMessage,
} from '@/utils/wsClient'

const REFRESH_EVENT_PREFIXES = [
  'order.',
  'tasks-map.',
  'tasks_map.',
  'schedule.',
  'dispatch.',
  'staff.assign',
  'route.',
] as const

const LOCATION_TYPES = new Set([
  'driver.location',
  'staff.location',
  'technician.location',
  'inspector.location',
  'location',
  'auth_ok',
])

const IGNORE_REFRESH_TYPES = new Set([
  'ping',
  'pong',
  'auth',
  'auth_ok',
  'hello',
  'subscribe',
  'location',
])

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object') return null
  return value as Record<string, unknown>
}

function readString(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim()
    if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  }
  return ''
}

function readNumber(...values: unknown[]): number | null {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) return value
    if (typeof value === 'string' && value.trim()) {
      const n = Number(value)
      if (Number.isFinite(n)) return n
    }
  }
  return null
}

function parseTaskId(value: string): number | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  const asNum = Number(trimmed)
  if (Number.isFinite(asNum)) return asNum
  const digits = trimmed.match(/\d+/g)
  if (!digits?.length) return null
  const last = Number(digits[digits.length - 1])
  return Number.isFinite(last) ? last : null
}

function parseLocationPayload(data: unknown): { staffId: string; point: TasksMapPoint } | null {
  const row = asRecord(data)
  if (!row) return null

  const nested = asRecord(row.location) ?? asRecord(row.position) ?? asRecord(row.coords)
  const lat = readNumber(row.lat, row.latitude, nested?.lat, nested?.latitude)
  const lng = readNumber(row.lng, row.lon, row.longitude, nested?.lng, nested?.lon, nested?.longitude)
  if (lat == null || lng == null) return null
  if (lat === 0 && lng === 0) return null
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null

  const staffId = readString(
    row.staff_id,
    row.staffId,
    row.user_id,
    row.userId,
    row.technician_id,
    row.technicianId,
    row.inspector_id,
    row.inspectorId,
    row.driver_id,
    row.driverId,
    row.id,
    nested?.staff_id,
    nested?.staffId,
    nested?.user_id,
    nested?.userId,
    nested?.technician_id,
    nested?.technicianId,
    nested?.inspector_id,
    nested?.inspectorId,
    nested?.driver_id,
    nested?.driverId,
    nested?.id,
  )
  if (!staffId) return null
  return { staffId, point: { lat, lng } }
}

function shouldRefreshBoard(type: string): boolean {
  const lower = type.toLowerCase()
  if (LOCATION_TYPES.has(lower) || IGNORE_REFRESH_TYPES.has(lower)) return false
  return REFRESH_EVENT_PREFIXES.some(
    (prefix) => lower.startsWith(prefix) || lower === prefix.replace(/\.$/, ''),
  )
}

export type TasksMapRealtimeOptions = {
  panelPeople: Ref<TasksMapPerson[]>
  selectedPersonId: Ref<string>
  selectedOrderId: Ref<string>
  panelTab: Ref<TasksMapPanelTab>
  refreshBoard: () => void | Promise<void>
  renderMap: () => void
}

function buildTrackingAuth(options: TasksMapRealtimeOptions): WsAuthPayload | null {
  const personId = options.selectedPersonId.value.trim()
  if (!personId) return null

  const person = options.panelPeople.value.find((item) => item.id === personId)
  if (!person) return null

  const selectedOrder =
    person.orders.find((order) => order.id === options.selectedOrderId.value) ?? person.orders[0]
  if (!selectedOrder) return null

  const taskId =
    parseTaskId(selectedOrder.id) ??
    parseTaskId(selectedOrder.orderId) ??
    parseTaskId(String(selectedOrder.sequence))
  if (taskId == null) return null

  const role = options.panelTab.value === 'inspectors' ? 'inspector' : 'technician'
  return {
    type: 'auth',
    role,
    token: personId,
    task_type: 'order',
    task_id: taskId,
  }
}

function buildAdminAuth(): WsAuthPayload | null {
  const token = localStorage.getItem('token')?.trim()
  if (!token) return null
  return { type: 'auth', role: 'admin', token }
}

export function useTasksMapRealtime(options: TasksMapRealtimeOptions): Ref<WsConnectionStatus> {
  const status = ref<WsConnectionStatus>('idle')
  let client: WsClient | null = null
  let refreshTimer: ReturnType<typeof setTimeout> | null = null
  let lastAuthKey = ''
  const unsubs: Array<() => void> = []

  function scheduleBoardRefresh(): void {
    if (refreshTimer) clearTimeout(refreshTimer)
    refreshTimer = setTimeout(() => {
      refreshTimer = null
      void options.refreshBoard()
    }, 400)
  }

  function applyDriverLocation(message: WsIncomingMessage): void {
    const parsed =
      parseLocationPayload(message.data) ??
      parseLocationPayload(message.raw) ??
      parseLocationPayload(asRecord(message.raw)?.location)
    if (!parsed) return

    const index = options.panelPeople.value.findIndex(
      (person) =>
        person.id === parsed.staffId ||
        person.name === parsed.staffId ||
        String(person.id) === parsed.staffId,
    )
    if (index < 0) {
      // Staff not on current tab/date — soft refresh so new presence can appear
      scheduleBoardRefresh()
      return
    }

    const people = [...options.panelPeople.value]
    const person = people[index]!
    people[index] = { ...person, position: parsed.point }
    options.panelPeople.value = people
    options.renderMap()
  }

  function handleMessage(message: WsIncomingMessage): void {
    const type = message.type.toLowerCase()
    if (LOCATION_TYPES.has(type)) {
      applyDriverLocation(message)
      return
    }
    if (shouldRefreshBoard(message.type)) {
      scheduleBoardRefresh()
    }
  }

  function resolveAuthPayload(): WsAuthPayload | null {
    return buildTrackingAuth(options) ?? buildAdminAuth()
  }

  function authKeyOf(payload: WsAuthPayload | null): string {
    if (!payload) return ''
    return [payload.role, payload.token, payload.task_type ?? '', String(payload.task_id ?? '')].join(
      '|',
    )
  }

  /** When selection changes, re-send auth so server returns that task's location. */
  function syncTrackingAuth(): void {
    if (!client || client.readyState !== WebSocket.OPEN) return
    const payload = resolveAuthPayload()
    const key = authKeyOf(payload)
    if (!payload || key === lastAuthKey) return
    lastAuthKey = key
    console.info('[ws] tracking auth', {
      role: payload.role,
      token: payload.token,
      task_type: payload.task_type,
      task_id: payload.task_id,
    })
    client.sendAuth()
  }

  onMounted(() => {
    client = new WsClient({
      getAuthPayload: resolveAuthPayload,
      subscribeRooms: ['admin', 'tasks-map', 'dispatch'],
      onStatus: (next) => {
        status.value = next
        if (next === 'open') {
          lastAuthKey = authKeyOf(resolveAuthPayload())
        }
      },
    })
    unsubs.push(client.onAny(handleMessage))
    client.connect()
  })

  watch(
    [
      options.selectedPersonId,
      options.selectedOrderId,
      options.panelTab,
      () => options.panelPeople.value.map((person) => person.id).join(','),
    ],
    () => {
      syncTrackingAuth()
    },
  )

  onUnmounted(() => {
    if (refreshTimer) {
      clearTimeout(refreshTimer)
      refreshTimer = null
    }
    unsubs.splice(0).forEach((off) => off())
    client?.disconnect()
    client = null
  })

  return status
}
